import type { AuthUser } from '@/nordy/interfaces'
import { SubscriptionKind } from '@/nordy/interfaces'
import type { CreditExhaustedGtmPayload } from './creditExhaustedTracking'

export type NordyGtmUser = Pick<
  AuthUser,
  | '_id'
  | 'signupSuccess'
  | 'subscriptionItemName'
  | 'subscriptionItemKind'
  | 'price_type'
>

export type NordyLoginMethod = 'google' | 'discord'
export type NordyCurrentUserPlan =
  | 'free'
  | 'standard'
  | 'advanced'
  | 'pro'
  | 'credit'
  | 'unknown'

type NordyPlanSource = Omit<NordyGtmUser, '_id' | 'signupSuccess'>

type NordyWorkflowTrackingSource = {
  workflowName: string
  workflowId: string
  isPersisted: boolean
}

const LOGIN_METHOD_KEY = 'nordy.gtm.login_method'
const PAID_PLANS = ['standard', 'advanced', 'pro'] as const

type NordyPaidPlan = (typeof PAID_PLANS)[number]

function isNordyPaidPlan(value: string): value is NordyPaidPlan {
  return PAID_PLANS.some((plan) => plan === value)
}

function pushDataLayer(payload: Record<string, unknown>): void {
  try {
    if (typeof window === 'undefined') return
    window.dataLayer ??= []
    window.dataLayer.push(payload)
  } catch {
    return
  }
}

function readLoginMethod(): NordyLoginMethod | undefined {
  try {
    const method = localStorage.getItem(LOGIN_METHOD_KEY)
    return method === 'google' || method === 'discord' ? method : undefined
  } catch {
    return undefined
  }
}

function writeLoginMethod(method: NordyLoginMethod): void {
  try {
    localStorage.setItem(LOGIN_METHOD_KEY, method)
  } catch {
    return
  }
}

function clearLoginMethod(): void {
  try {
    localStorage.removeItem(LOGIN_METHOD_KEY)
  } catch {
    return
  }
}

export function normalizeNordyCurrentUserPlan(
  user: NordyPlanSource
): NordyCurrentUserPlan {
  const itemName = user.subscriptionItemName?.toLowerCase()

  if (itemName && isNordyPaidPlan(itemName)) return itemName
  if (user.price_type === 'credit') return 'credit'
  if (
    itemName === 'free' ||
    user.subscriptionItemKind === SubscriptionKind.Free ||
    user.price_type === 'free'
  ) {
    return 'free'
  }

  return 'unknown'
}

export function pushNordyUserContext(user: NordyGtmUser): void {
  const loginMethod = readLoginMethod()

  pushDataLayer({
    user_id: user._id,
    is_logged_in: true,
    current_user_plan: normalizeNordyCurrentUserPlan(user),
    ...(loginMethod ? { login_method: loginMethod } : {})
  })
}

export function trackNordyAuth(
  user: NordyGtmUser,
  method: NordyLoginMethod,
  location: string
): void {
  writeLoginMethod(method)
  pushDataLayer({
    event: user.signupSuccess ? 'sign_up' : 'login_success',
    user_id: user._id,
    is_logged_in: true,
    current_user_plan: normalizeNordyCurrentUserPlan(user),
    login_method: method,
    location
  })
}

export function trackNordyLogout(): void {
  clearLoginMethod()
  pushDataLayer({
    event: 'logout',
    user_id: undefined,
    is_logged_in: false,
    current_user_plan: 'unknown'
  })
}

export function trackNordyGenerateCreate(
  workflow: NordyWorkflowTrackingSource | null
): void {
  pushDataLayer({
    event: 'generate_create',
    creation_type: 'comfyui',
    model: workflow?.workflowName ?? 'untitled',
    easyapp_name: 'n/a',
    workflow_id: workflow?.isPersisted ? workflow.workflowId : undefined
  })
}

export function trackNordyCreditExhausted(
  payload: CreditExhaustedGtmPayload
): void {
  pushDataLayer(payload)
}
